import * as _ from "lodash";

import { ExtensionBase } from "../extensionBase";
import { viewerOptions } from "../../viewer";
import { UiUtils } from "../../common/uiUtils";
import * as SmartDrawings from "../../smartDrawings";
import { GfxUtils } from "../../common/gfxUtils";
import { CustomColorExtension } from "../customColor/customColorExtension";

// Extension that adds the custom 3D elements for nodes, toggles their visibility and handles the connected element's highlight 
// on node selection.
export class NodeExtension extends ExtensionBase {

    static extensionName = "NodeExtension";
  
    private subToolbar: Autodesk.Viewing.UI.ControlGroup;
    private nodeButton: Autodesk.Viewing.UI.Button;
    private nodesVisible = false;

    constructor(viewer: Autodesk.Viewing.GuiViewer3D, options: any) {
        super(viewer, options);
        viewer.addEventListener(SmartDrawings.SdEvents.EXTERNAL_DATA_LOADED, this.onExternalDataLoaded);
    }

    static registerExtension() {
        super.registerExtension(NodeExtension.extensionName, NodeExtension);
    }

    // Don't know if external data or toolbar exists first, so cover both cases.
    onToolbarCreated() {
        this.initNodes();
    }

    private onExternalDataLoaded = () => {
        this.initNodes();
    }

    private async initNodes() {
        if (this.nodeButton == null && viewerOptions.externalData?.nodes != null) {

            const nodeGfxUtils = new GfxUtils(this.viewer, "Nodes");
            await nodeGfxUtils.drawConnectingNodes(viewerOptions.externalData.nodes.propertyValues);
            this.setupSelectedNodeElementHighlight();
            this.hideNodes();

            this.nodeButton = UiUtils.createButton({
                id: "SmartDrawings.NodeButton",
                tooltip: "Display nodes",
                iconClass: "ion-md-git-commit",
                onClick: () => {
                    this.nodesVisible = !this.nodesVisible;
                    this.nodeButton.setState(this.nodesVisible ? Autodesk.Viewing.UI.Button.State.ACTIVE : Autodesk.Viewing.UI.Button.State.INACTIVE)

                    if (this.nodesVisible) {
                        this.showNodes();
                    } else {
                        this.hideNodes();
                    }
                }
            });

            this.subToolbar = this.getControlGroup("SmartDrawings.Extensions");
            this.subToolbar.addControl(this.nodeButton);
        }
    }

    private setupSelectedNodeElementHighlight() {
        this.viewer.addEventListener(Autodesk.Viewing.AGGREGATE_SELECTION_CHANGED_EVENT, ({ selections }) => {
            // If custom colors are active, don't mess them up.
            if (this.customColorsDisplayed()) {
                return;
            }

            this.viewer.clearThemingColors(this.getPrimaryModel());
            const nodeSel = _.find(selections, sel => sel.model.id === 2);
            if (nodeSel != null) {
                for (let dbId of nodeSel.dbIdArray) {
                    const elementGuids = viewerOptions.externalData.nodes.propertyValues[dbId]["ELEMENT_GUID"].split(",");
                    const elementDbIds = viewerOptions.idMap.nodeIds(null, elementGuids.map(eg => eg.trim()));
                    for (let ed of elementDbIds) {
                        this.viewer.setThemingColor(ed,
                            new THREE.Vector4(255, 0, 0, 1),
                            this.getPrimaryModel(),
                            true
                        );
                    }
                }
            }
        });
    }

    private customColorsDisplayed() {
        const colorExtension = this.viewer.getExtension(CustomColorExtension.extensionName) as CustomColorExtension;
        return colorExtension != null && colorExtension.isActive();
    }

    private getPrimaryModel() {
        return this.viewer.getVisibleModels()[0];
    }

    private getNodeModel() {
        return this.viewer.getVisibleModels()[1];
    }

    private showNodes() {
        this.viewer.isolate([], this.getNodeModel());
    }

    private hideNodes() {
        // Made-up dbId to satisfy the viewer.
        this.viewer.isolate([1000000], this.getNodeModel());
        if (!this.customColorsDisplayed()) {
            this.viewer.clearThemingColors(this.getPrimaryModel());
        }
    }
}